// =============================================================
// LANDING COPY — "BİR GÜN FUAR ALANINDA" · tek kaynak · typed
// Mimari plan: content/landing-copy.ts + scene-map.ts
// =============================================================

export interface TitleToken {
  readonly text: string;
  readonly italic?: boolean;
}

export interface PackageSpec {
  readonly id: string;
  readonly name: string;
  readonly level: string;
  readonly tagline: string;
  readonly image: string;
  readonly spec: readonly string[];
  readonly accent?: boolean;
}

export interface PlateRow extends PackageSpec {
  readonly col: 1 | 2;
  readonly offsetY: number;
}

export interface TestimonialRow {
  readonly kind: "feature" | "tile";
  readonly src: string;
  readonly alt: string;
  readonly quote: string;
  readonly name: string;
  readonly org: string;
  readonly period: string;
}

export interface ServisStep {
  readonly time: string;
  readonly label: string;
  readonly count: string;
  readonly active: boolean;
}

export interface Counter {
  readonly num: number;
  readonly suffix: string;
  readonly label: string;
}

export const COPY = {
  brand: {
    name: "FuarCafe",
    tagline: "Catering · Since 2014",
    established: "EST · 2014",
  },

  nav: [
    { label: "Şafak",    href: "#safak" },
    { label: "Hazırlık", href: "#hazirlik" },
    { label: "Sofra",    href: "#sofra" },
    { label: "Servis",   href: "#servis" },
    { label: "Anı",      href: "#ani" },
    { label: "Yankı",    href: "#yanki" },
    { label: "Kapı",     href: "#kapi" },
  ] as const,

  safak: {
    chapter: "01 · Şafak",
    chapterShort: "01",
    eyebrow: "Bir gün başlıyor",
    titleTokens: [
      { text: "Fuarların" },
      { text: " ", italic: true },
      { text: "kalbi, " },
      { text: "masaların", italic: true },
      { text: " sessiz ritüeli." },
    ] as readonly TitleToken[],
    fullTitleText: "Fuarların kalbi, masaların sessiz ritüeli.",
    sub: "Ziyaretçinin bir fincan kahvenin buharında durduğu, lezzetin markanın hikâyesini tamamladığı o anı tasarlıyoruz. Şafak, bu hikâyenin başladığı saat.",
    ctaPrimary: { label: "Hazırlığı gör", href: "#hazirlik" },
    ctaSecondary: { label: "Bizi ara", href: "tel:+905324403696" },
    meta: [
      { label: "Konum", value: "İstanbul" },
      { label: "Servis", value: "7/24" },
    ] as const,
  },

  hazirlik: {
    chapter: "02 · Hazırlık",
    chapterShort: "02",
    eyebrow: "T-06:00",
    title: "Standınız kurulmadan altı saat önce.",
    body: "Bu bir başlangıç değil — bir hazırlık ritüeli. Soğuk zincirden servis hattına, porselenden çiçeğe kadar her adım görünmez bir senfoni. Ekip bu senfoninin orkestrasıdır.",
    timeline: [
      { time: "T-06:00", label: "Yükleme · Soğuk zincir",      count: "1 ekip" },
      { time: "T-04:00", label: "Stand kurulumu · Servis hattı", count: "3 ekip" },
      { time: "T-02:00", label: "Yerleştirme · Porselen & cam",  count: "5 masa" },
      { time: "T-01:00", label: "İlk servis · Sıcak içecekler",   count: "10 kişi" },
      { time: "T-00:00", label: "Açılış · Ziyaretçi karşılama",    count: "Kapı açık" },
      { time: "T+her saat", label: "Sürekli yenileme · Taze tabak", count: "∞" },
    ] as const,
  },

  sofra: {
    chapter: "03 · Sofra",
    chapterShort: "03",
    eyebrow: "Her bütçeye uygun menü seçenekleri",
    title: "Güvenilir markalarla taze ikramlıklarla güleryüzlü servis.",
    lede: "İhtiyacınıza göre seçin — menü içerikleri ve detaylar için WhatsApp'tan yazın.",
    whatsappBase: "https://wa.me/905324403696",
    packages: [
      { id: "eko",           name: "Eko",           level: "Çay & Temel İkram",         tagline: "Çay servisi",        image: "img/9c75426c-6548-4af1-bf37-cfc52c47c418.jpeg",  spec: ["Sınırsız soğuk içecek", "Kahve · Çay", "Temel ikram"],                  col: 1, offsetY: 0 },
      { id: "standart",      name: "Standart",      level: "Kurabiye & Atıştırmalık",   tagline: "Tatlı & tuzlu",       image: "img/f80f0754-eb03-42e4-846c-82728afa38a3.jpeg",  spec: ["Tatlı & tuzlu kurabiye", "Kahve çeşitleri", "Meyve & atıştırmalık"],   col: 2, offsetY: 16 },
      { id: "standart-plus", name: "Standart Plus", level: "Mini Sandviç & Kanape",     tagline: "Sandviç servisi",     image: "img/61558c85-6dc6-495e-941b-84b70b7daf07.jpeg",  spec: ["Mini sandviç", "Kanape çeşitleri", "Geniş içecek"],                col: 1, offsetY: 32 },
      { id: "silver",        name: "Silver",        level: "Kanape & Kuruyemiş",        tagline: "Kanape",              image: "img/4368de8b-b388-40aa-9214-84e32434fa9f.jpeg",  spec: ["Kanape", "Kuruyemiş", "Meyve suyu & soda"],                            col: 2, offsetY: 12 },
      { id: "silver-plus",   name: "Silver Plus",   level: "Sıcak Kanape & Geniş Meyve",tagline: "Sıcak atıştırmalık",  image: "img/267f4394-e2b6-44b9-8285-5b4256aa7706.jpeg",  spec: ["Sıcak kanape", "Geniş meyve", "Çay & kahve"],                            col: 1, offsetY: -8 },
      { id: "gold",          name: "Gold",          level: "Tatlı & Sıcak Çikolata",    tagline: "Tatlı servisi",       image: "img/5f4cf5c9-e7a4-4f17-8311-fb293b0ae1f8.jpeg",  spec: ["Renkli patates topları", "İkram şekerleri", "Sıcak çikolata"],         col: 2, offsetY: 24 },
      { id: "bronz",         name: "Bronz",         level: "Geniş Kanape & Tatlı Çeşitleri", tagline: "Geniş atıştırmalık", image: "img/1f970dc8-7716-4c46-9040-4673f767132e.jpeg",  spec: ["Geniş kanape", "Tatlı çeşitleri", "Tam içecek"],                    col: 1, offsetY: 20 },
      { id: "vip",           name: "VIP",           level: "Türk Kahvesi & Özel Sunum", tagline: "Türk kahvesi",        image: "img/63c22b7b-9522-488c-bb0a-fe2b5d84d7b1.jpeg",  spec: ["Özel sunum", "Sıcak çikolata", "Salep & Türk kahvesi"],                col: 2, offsetY: 4 },
      { id: "vip-plus",      name: "VIP Plus",      level: "İçki & Meze Yayılımı",      tagline: "Meze servisi",        image: "img/ed57f848-dda2-429a-ad94-494a637ea05f.jpeg",  spec: ["Yerli & yabancı içki", "Börek & zeytinyağlı", "Genişletilmiş menü"],     col: 1, offsetY: -12 },
      { id: "platin",        name: "Platin",        level: "Tüm Hizmetlerin Birleşimi", tagline: "İmza hizmet",         image: "img/db97226d-d059-43d7-8baf-b6d633957827.jpeg",  spec: ["Tüm paketlerin birleşimi", "Custom sunum", "Sınırsız ziyaretçi"],      col: 1, offsetY: 0, accent: true },
    ] as readonly PlateRow[],
  },

  servis: {
    chapter: "04 · Servis",
    chapterShort: "04",
    eyebrow: "Bilanço · On iki yıl",
    title: "Görünmeyen bir senfoni.",
    body: "Sahnede müzisyen yok — ekibimiz var. Sahne arkasında servis, soğuk zincir, planlama, geri dönüşüm. Her görevlinin arkasında yılların eğitimi.",
    counters: [
      { num: 240,    suffix: "+",   label: "Tamamlanan Fuar" },
      { num: 4_800_000, suffix: "",  label: "Servis Edilen Ziyaretçi" },
      { num: 12,     suffix: " yıl", label: "Sektör Deneyimi" },
      { num: 4.9,    suffix: " /5", label: "Müşteri Memnuniyeti" },
    ] as readonly Counter[],
  },

  ani: {
    chapter: "05 · Anı",
    chapterShort: "05",
    eyebrow: "Referans Galeri",
    title: "Her stand bir masaya dönüşür.",
    caption: "Her projede görünmez bir senfoni var — masa hazırlığından son bardağa kadar. İşte bazıları.",
    featureImage: "img/referansGaleri/20c9e58f-f345-4e2a-ad2a-e17b653e1f70.jpeg",
    featureAlt: "Sürmene Hancıoğlu Çamburnu standında iki kadın hostes ve erkek yönetici, ahşap standın önünde",
    gallery: [
      // ── Sayfa 1 ──
      {
        kind: "hero",
        src: "media/gallery-1.jpg",
        alt: "Açık hava catering yayılımı — uzun masa, kurumsal zirve",
        client: "Kurumsal Zirve",
        year: "2024",
        scope: "Akşam yemeği · 240 kişi",
      },
      {
        kind: "tile",
        src: "media/gallery-2.jpg",
        alt: "Bacon yüklü patates — fine dining sunumu",
        client: "Gala Yemeği",
        year: "2024",
        scope: "Fine dining · 180 kişi",
      },
      {
        kind: "tile",
        src: "img/referansGaleri/20c9e58f-f345-4e2a-ad2a-e17b653e1f70.jpeg",
        alt: "Sürmene Hancıoğlu Çamburnu standı — hostesler servis alanında",
        client: "Sürmene · Hancıoğlu",
        year: "2024",
        scope: "EMITT Fuar standı · 5 gün",
      },
      // ── Sayfa 2 ──
      {
        kind: "hero",
        src: "media/gallery-3.jpg",
        alt: "Üç kadeh kahve — açılış jesti",
        client: "Teknoloji Fuarı",
        year: "2024",
        scope: "Stand içi · 4 gün",
      },
      {
        kind: "tile",
        src: "media/gallery-4.jpg",
        alt: "Balık tabak — fine dining",
        client: "Yılbaşı Galası",
        year: "2023",
        scope: "Akşam yemeği · 180 kişi",
      },
      {
        kind: "tile",
        src: "media/gallery-5.jpg",
        alt: "Su kenarı mekan — panoramik atmosfer",
        client: "Su kenarı · Restoran",
        year: "2025",
        scope: "Mekan organizasyonu · 200 kişi",
      },
      // ── Sayfa 3 ──
      {
        kind: "hero",
        src: "media/gallery-6.jpg",
        alt: "Catering sunumu — düzenli masa",
        client: "Kurumsal Lansman",
        year: "2024",
        scope: "Açılış kokteyli · 350 kişi",
      },
      {
        kind: "tile",
        src: "media/gallery-7.jpg",
        alt: "Yiyecek istasyonu — yayılım",
        client: "Festival alanı",
        year: "2024",
        scope: "Açık hava · 2 gün",
      },
      {
        kind: "tile",
        src: "media/gallery-8.jpg",
        alt: "Davet atmosferi — yakın plan",
        client: "Ödül Gecesi",
        year: "2024",
        scope: "Gala · 600 kişi",
      },
      // ── Sayfa 4 ──
      {
        kind: "hero",
        src: "media/gallery-9.jpg",
        alt: "Zengin sofra — masa düzeni",
        client: "Düğün · Butik",
        year: "2023",
        scope: "Akşam yemeği · 150 kişi",
      },
      {
        kind: "tile",
        src: "media/gallery-10.jpg",
        alt: "Meze yayılımı — küçük tabaklar",
        client: "Meze gecesi",
        year: "2024",
        scope: "Stand-up · 120 kişi",
      },
      {
        kind: "tile",
        src: "media/gallery-11.jpg",
        alt: "Sunum detayı — servis anı",
        client: "Şirket içi etkinlik",
        year: "2025",
        scope: "Toplantı catering · 90 kişi",
      },
    ],
    pagination: {
      perPage: 3,
      prevLabel: "Önceki",
      nextLabel: "Sonraki",
      counterTemplate: "Sayfa {current} / {total}",
    },
    marginalia: [
      "Yüzlerce aktivasyon",
      "On binlerce ziyaretçi",
      "Sıfır fire",
      "12 yıllık deneyim",
    ],
  },

  yanki: {
    chapter: "06 · Yankı",
    chapterShort: "06",
    eyebrow: "Müşterilerimiz ne diyor",
    title: "Sessiz ama kalıcı bir iz.",
    caption: "Fuar bittiğinde geriye ne kalır? Bizim için — yeniden aranan bir telefon, bir sonraki yıl için ayırtılan tarih, ve bir markanın sahnede nasıl göründüğüne dair sessiz güven.",
    testimonials: [
      {
        kind: "feature",
        src: "img/referansGaleri/20c9e58f-f345-4e2a-ad2a-e17b653e1f70.jpeg",
        alt: "EMITT Fuar standında FuarCafe hostesleri",
        quote: "Fuarlarımızın kalabalığı arasında FuarCafe sadece bir tedarikçi değil — bir ev sahibi. Ziyaretçi onları gördüğünde markamız daha güvenli hissediyor.",
        name: "Etkinlik Direktörü",
        org: "Teknoloji Fuarı",
        period: "2023–2026",
      },
      {
        kind: "tile",
        src: "media/gallery-9.jpg",
        alt: "Kurumsal gala yemeği — düzenli masa",
        quote: "Üç yıldır birlikte çalışıyoruz — her seferinde daha önceki yıldan iyi.",
        name: "Pazarlama Müdürü",
        org: "Kurumsal Zirve",
        period: "2023–2026",
      },
      {
        kind: "tile",
        src: "media/gallery-3.jpg",
        alt: "Stand içi kahve servisi — jest",
        quote: "Açılış kahvemizden gala yemeğimize kadar uçtan uca vardılar. Aradaki fark, gözümüzün arkasında kaldı.",
        name: "Operasyon Direktörü",
        org: "Sürmene · Hancıoğlu",
        period: "2024–2026",
      },
    ] as readonly TestimonialRow[],
  },

  kapi: {
    chapter: "07 · Kapı",
    chapterShort: "07",
    title: "Birlikte tasarlayalım.",
    sub: "Bir sonraki fuar, bir sonraki lansman, bir sonraki zirve. Fikirlerinizi dinliyoruz — yemeği birlikte inşa ederiz.",
    phone: "0532 440 36 96",
    phoneHref: "tel:+905324403696",
    ctaLabel: "Şimdi ara",
    hours: "7/24 · Pzt — Paz",
  },

  colophon: {
    brandHtml: 'Fuar Cafe',
    address: ["İstanbul · Türkiye", "Tüm yurt içi & dışı fuarlar"],
    contact:  ["0532 440 36 96", "info@fuarcafe.example"],
    social:   ["Instagram", "Facebook", "WhatsApp"],
    legal: "© 2026 Fuar Cafe Catering · Tüm hakları saklıdır.",
    attribution: {
      label: "yabidev.com tarafından tasarlanmıştır",
      href: "https://yabidev.com",
    },
    seal:  "Bir gün daha · 2026",
    locale: "İstanbul · Türkiye",
  },
} as const;